import React, { useState, useEffect, useCallback } from 'react';
import { useFormik, FormikHelpers } from 'formik';

type DirtyFields<FormValues> = {
  [Key in keyof FormValues]?: boolean;
};

/**
 * This formik hook provides functionality out of the box for setting and getting dirty fields.
 *
 * @param {*} initialValues - Initial values for the form, dirty fields are initialized based off the field names.
 * @param {*} validate - Validation function that formik will run when the form is submitted or a field is changed/blurred.
 * @param {*} onSubmitHandler - Called when formik form is submitted AND has passed validation.
 */
export default function useForm<FormValues extends object>(
  initialValues: FormValues,
  validate: (values: FormValues) => object,
  onSubmitHandler: (values: FormValues, formikBag: FormikHelpers<FormValues>) => void,
) {
  const onSubmit = (values: FormValues, formikBag: FormikHelpers<FormValues>) => {
    // This may seem redundant but is necessary so that formik does not automatically setSubmitting to false for us.
    onSubmitHandler(values, formikBag);
  };

  const [dirtyFields, setDirtyFields] = useState<DirtyFields<FormValues>>({});
  const formik = useFormik<FormValues>({ initialValues, validate, onSubmit });

  const getBoolValForDirtyFields = (vals: DirtyFields<FormValues> | FormValues, bool: boolean) => {
    const newDirtyFieldVals: DirtyFields<FormValues> = {};
    Object.keys(vals).forEach((key) => {
      newDirtyFieldVals[key as keyof FormValues] = bool;
    });

    return newDirtyFieldVals;
  };

  useEffect(() => {
    const dirtyFieldsList: DirtyFields<FormValues> = getBoolValForDirtyFields(initialValues, false);
    setDirtyFields(dirtyFieldsList);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    const newDirtyFieldVals = getBoolValForDirtyFields(dirtyFields, true);
    setDirtyFields(newDirtyFieldVals);

    formik.handleSubmit(e);
  };

  const handleChange = useCallback((e: React.ChangeEvent<any>) => {
    const fieldName = e.target.name;
    setDirtyFields((prevState) => ({ ...prevState, [fieldName]: true }));

    formik.handleChange(e);
  }, []);

  return {
    formik, dirtyFields, handleChange, handleSubmit,
  };
}
